// Maggi — Factory Process Decoder (factory_files_decoder, Hindi).
// 16fps · 1280×704 · ~865s. Universal padding 22/18, all white, no danda.
// Logo composited at ffmpeg stage (modal_factory_render spec.add_logo).
// CTA channel = "FACTORY PROCESS DECODER" (channel_config.channel_name).
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { PaletteProvider, BarCompare, BigStat, Tag } from "./overlayKit";
import { MAGGI_PALETTE } from "./MaggiFullPreviewPalette";
import { CTASubscribeBell } from "./cta/CTASubscribeBell";
import { CTACombinedBar } from "./cta/CTACombinedBar";

const FPS = 16;
const f = (s: number) => Math.round(s * FPS);

// Custom red SUBSCRIBE pill that covers the burned-in subscribe button in clip 50000.
// Slide-in from below 0→0.4s, hold full opacity through 3.0s, fade-out 3.0→4.0s.
// Position locked at top:570 (preview-approved).
const clamp = { extrapolateLeft: "clamp" as const, extrapolateRight: "clamp" as const };

const SubscribeCover: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = frame / fps;
  const y       = interpolate(t, [0, 0.4], [220, 0], clamp);
  const opacity = interpolate(t, [3.0, 4.0], [1, 0], clamp);
  return (
    <AbsoluteFill>
      <div
        style={{
          position: "absolute",
          left: 400,
          top: 570,
          width: 480,
          height: 110,
          transform: `translateY(${y}px)`,
          opacity,
          background: "#cc0000",
          borderRadius: 999,
          boxShadow: "0 6px 28px rgba(0,0,0,0.55), 0 0 0 2px rgba(255,255,255,0.08) inset",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          color: "#ffffff",
          fontFamily: "Inter, sans-serif",
          fontWeight: 900,
          fontSize: 44,
          letterSpacing: 2,
          textShadow: "0 2px 8px rgba(0,0,0,0.6)",
        }}
      >
        <span style={{ fontSize: 38, lineHeight: 1, transform: "translateY(2px)" }}>▶</span>
        SUBSCRIBE
      </div>
    </AbsoluteFill>
  );
};

export const MaggiFullPreview: React.FC = () => (
  <PaletteProvider value={MAGGI_PALETTE}>
    <AbsoluteFill>

      {/* 00:22 — 2015 (lead-recall year teaser). Name only. */}
      <Tag inS={22.0} outS={25.5} label="2015" color="primary" />

      {/* 00:40.157 – 00:44.157 — custom red SUBSCRIBE pill covering the burned-in
          subscribe button. Slide-in 0→0.4s, hold, fade-out 3.0→4.0s. Approved via
          MaggiSubCoverPreview. */}
      <Sequence from={f(40.157)} durationInFrames={f(4.0)} layout="none">
        <SubscribeCover />
      </Sequence>

      {/* 01:15 — 843.84 MILLION MT global wheat. */}
      <BigStat value={843.84} suffix=" M MT" valueS={75.0} decimals={2} outS={79.0} />

      {/* 02:02 — 32% water in brine (28-32% control range). */}
      <BigStat value={32} suffix="% WATER" valueS={122.5} decimals={0} outS={125.5} />

      {/* 02:10 — 15 MIN mixing time (10-15 range). */}
      <BigStat value={15} suffix=" MIN" valueS={129.93} decimals={0} outS={132.5} />

      {/* 02:56 — 2 MM initial sheet thickness (0.8 – 2 mm). */}
      <BigStat value={2} suffix=" MM" valueS={176.0} decimals={0} outS={179.5} />

      {/* 03:36 — NESTLÉ · 1947 (Nestlé bought Maggi). */}
      <Tag inS={216.0} outS={220.0} label="NESTLÉ · 1947" color="primary" />

      {/* 03:47 — MOMOFUKU ANDO · OSAKA · 1958 (first instant noodle). */}
      <Tag inS={227.0} outS={232.0} label="MOMOFUKU ANDO · OSAKA · 1958" color="primary" />

      {/* 04:22 — $62 BILLION global instant-noodle industry. */}
      <BigStat value={62} prefix="$" suffix=" B" valueS={262.5} decimals={0} outS={266.0} />

      {/* 04:46 — 7 ROLLER STAGES (5-7 range). */}
      <BigStat value={7} suffix=" ROLLERS" valueS={286.0} decimals={0} outS={289.5} />

      {/* 04:58 — FINAL SHEET 1.2 MM (0.8-1.2 final thickness). */}
      <BigStat value={1.2} suffix=" MM" valueS={298.5} decimals={1} outS={302.0} />

      {/* 07:00 — 105°C steam (100-105 saturated steam temperature). */}
      <BigStat value={105} suffix=" °C" valueS={420.2} decimals={0} outS={423.5} />

      {/* 07:08 — 3 MIN steaming time (1-3 range). */}
      <BigStat value={3} suffix=" MIN" valueS={425.5} decimals={0} outS={429.0} />

      {/* 07:26 — 100 G blocks (60-100g portions). */}
      <BigStat value={100} suffix=" G" valueS={446.0} decimals={0} outS={449.5} />

      {/* 07:40 — 2 SEC defect detection. */}
      <BigStat value={2} suffix=" SEC" valueS={460.0} decimals={0} outS={463.0} />

      {/* 07:58 — 160°C palm-oil frying temperature (140-160 range). */}
      <BigStat value={160} suffix=" °C" valueS={478.0} decimals={0} outS={482.0} />

      {/* 08:14 — Moisture drop chart: 40% → 6% via flash frying. */}
      <BarCompare
        v1={40} v2={6}
        v1Label="40% MOISTURE" v2Label="2 – 6%"
        l1S={494.76} l2S={497.83} outS={500.5}
      />

      {/* 09:58 — JUNE 5, 2015 · FSSAI RECALL. */}
      <Tag inS={598.0} outS={604.0} label="JUNE 5 · 2015 · FSSAI" color="primary" />

      {/* 10:09 — 9 VARIANTS recalled. */}
      <BigStat value={9} suffix=" VARIANTS" valueS={609.0} decimals={0} outS={612.5} />

{/* 12:38 — 121.2 BILLION servings in 2023. "SERVINGS · 2023" on second line. */}
      <BigStat value={121.2} suffix=" B" sub="SERVINGS · 2023" valueS={758.0} decimals={1} outS={762.5} />

      {/* 12:44 — 332 MILLION packets per day. */}
      <BigStat value={332} suffix=" M / DAY" valueS={764.5} decimals={0} outS={768.5} />

      {/* 14:10 — channel-crafted LIKE·SHARE·SUBSCRIBE end bar (shifted from 13:50). */}
      <Sequence from={f(850.0)} durationInFrames={f(5.0)} layout="none">
        <CTACombinedBar />
      </Sequence>

    </AbsoluteFill>
  </PaletteProvider>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
